/**
 * Shared completion logic for a social (OAuth) sign-in, used by BOTH the web
 * callback route (`routes/_auth/social-callback.tsx`) and the native deep-link
 * provider (`providers/deep-linking-provider.tsx`). Factoring it here keeps the
 * token-handling in ONE place:
 *   - web: authenticates the follow-up `get-session` via the device cookie the
 *     server just set (no token in JS);
 *   - Tauri legacy (in-WebView `#token` redirect): attaches the fragment token
 *     as an explicit Bearer;
 *   - native deep-link (Phase 4): the token was stored + primed in Rust from the
 *     App/Universal Link, so `get-session` succeeds via the Rust-injected Bearer
 *     WITHOUT the secret ever entering JS; `onUserResolved` re-keys the legacy
 *     store slot to the account's `userId`.
 */

import { msg } from "@lingui/core/macro";
import type { useNavigate } from "@tanstack/solid-router";
import { queryKeys, safeRedirectTarget, sharedApi } from "@bso/shared";
import { api } from "@/lib/api";
import { registerAccountFromResponse } from "@/lib/auth";
import { readAccountsStateSync } from "@/lib/accounts";
import { indexOfUserId, rememberLastActiveIndex } from "@/lib/active-account";
import { queryClient } from "@/lib/query-client";
import { sessionActor } from "@/lib/session-machine";

type Translate = (message: ReturnType<typeof msg>) => string;
type Navigate = ReturnType<typeof useNavigate>;

/** Human-readable message for each error code the backend can send. */
export function socialErrorMessage(t: Translate, code: string): string {
  switch (code) {
    case "provider_denied": {
      return t(msg`You cancelled the sign-in. No changes were made.`);
    }
    case "provider_unavailable":
    case "exchange_failed": {
      return t(
        msg`The provider is temporarily unavailable. Please try again or use another method.`,
      );
    }
    case "account_conflict": {
      return t(msg`That social account is already linked to a different user.`);
    }
    case "email_required": {
      return t(msg`The provider did not share an email, which is required to sign in.`);
    }
    case "account_banned": {
      return t(msg`This account is not allowed to sign in.`);
    }
    case "invalid_flow":
    case "missing_flow":
    case "state_mismatch": {
      return t(msg`The sign-in session expired. Please try again.`);
    }
    default: {
      return t(msg`Could not complete the sign-in. Please try again.`);
    }
  }
}

/** Extract the user id from a get-session/auth response without type assertions. */
function extractUserId(res: unknown): string | null {
  if (typeof res !== "object" || res === null || !("user" in res)) {
    return null;
  }
  const { user } = res;
  if (typeof user !== "object" || user === null || !("id" in user) || typeof user.id !== "string") {
    return null;
  }
  return user.id;
}

export interface FinishSocialLoginOptions {
  navigate: Navigate;
  /** Safe redirect path (defaulted + sanitized by the caller or here). */
  redirect?: string;
  /**
   * Explicit Bearer for the follow-up `get-session` — the Tauri legacy
   * in-WebView path. Omitted on web (cookie) and on the native deep-link path
   * (Rust injects it).
   */
  oauthToken?: string;
  /**
   * Native deep-link hook: invoked with the resolved `userId` AFTER
   * `get-session` but BEFORE registering the account, so the token can be
   * re-keyed from the legacy store slot to the account slot (all in Rust).
   */
  onUserResolved?: (userId: string) => Promise<void>;
}

/**
 * Complete the login: fetch the session, register/refresh the account in the
 * multi-account registry, prime the machine + query cache, and land THIS tab on
 * the freshly authenticated account's `/u/<index>` (falling back to `redirect`).
 * Never throws — on failure it still navigates so the boot manager can resolve.
 */
export async function finishSocialLogin(options: FinishSocialLoginOptions): Promise<void> {
  const { navigate, oauthToken, onUserResolved } = options;
  const redirect = safeRedirectTarget(options.redirect ?? "/");
  let navigated = false;

  try {
    // Tauri legacy attaches `oauthToken` EXPLICITLY; web + native deep-link rely
    // on the cookie / Rust-injected Bearer respectively.
    const result = oauthToken
      ? await api.GET("/api/auth/get-session", {
          params: { query: { jwt: true } },
          headers: { Authorization: `Bearer ${oauthToken}` },
        })
      : await sharedApi.getSession(api);
    const data = result.data ?? null;

    const userId = extractUserId(data);
    if (userId && onUserResolved) {
      await onUserResolved(userId);
    }

    const account = await registerAccountFromResponse(data, oauthToken);
    sessionActor.send({ type: "SERVER_REPLY", session: data, error: null });
    queryClient.setQueryData(queryKeys.auth.session(), data);

    if (account) {
      const index = indexOfUserId(readAccountsStateSync(), account.userId);
      if (index !== null) {
        rememberLastActiveIndex(index);
        await queryClient.refetchQueries();
        void navigate({ to: "/u/$u", params: { u: index }, replace: true });
        navigated = true;
        return;
      }
    }
    await queryClient.refetchQueries();
  } catch {
    // Even if the refresh fails, the token is already persisted; let the boot
    // manager resolve the session on the destination route.
  } finally {
    if (!navigated) {
      void navigate({ to: redirect, replace: true });
    }
  }
}
